import { useState } from 'react'
import { cn } from '../utils'
import { User, UserMinus } from 'lucide-react'
import { motion } from 'framer-motion'
import type { OrgMember } from './types'

export type MemberListProps = {
  members: OrgMember[]
  onRemoveMember?: (memberId: string) => Promise<void>
  className?: string
}

function RoleBadge({ role }: { role: string }) {
  const isOwner = role === 'owner'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
        isOwner
          ? 'bg-primary/10 text-primary'
          : 'bg-secondary text-muted-foreground'
      )}
    >
      {role}
    </span>
  )
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const itemVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.25,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
}

export function MemberList({ members, onRemoveMember, className }: MemberListProps) {
  const [removingId, setRemovingId] = useState<string | null>(null)

  if (members.length === 0) {
    return (
      <motion.p
        className="text-sm text-muted-foreground py-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        No members yet.
      </motion.p>
    )
  }

  const handleRemove = async (memberId: string) => {
    if (!onRemoveMember) return
    setRemovingId(memberId)
    try {
      await onRemoveMember(memberId)
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <div className={cn('space-y-1', className)}>
      {members.map((member, i) => (
        <motion.div
          key={member.id}
          className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-card transition-colors"
          custom={i}
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="flex-shrink-0 rounded-full bg-secondary p-1.5">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {member.displayName}
            </p>
            <p className="text-xs text-muted-foreground truncate">{member.email}</p>
          </div>
          <RoleBadge role={member.role} />
          <span className="text-xs text-muted-foreground/60 flex-shrink-0">
            {formatDate(member.joinedAt)}
          </span>
          {onRemoveMember && (
            member.role !== 'owner' ? (
              <button
                type="button"
                onClick={() => handleRemove(member.id)}
                disabled={removingId === member.id}
                className="flex-shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
              >
                <UserMinus className="h-3 w-3" />
                {removingId === member.id ? 'Removing…' : 'Remove'}
              </button>
            ) : (
              <div className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px]" aria-hidden>
                <UserMinus className="h-3 w-3 opacity-0" />
                <span className="opacity-0 font-medium uppercase tracking-wider">Remove</span>
              </div>
            )
          )}
        </motion.div>
      ))}
    </div>
  )
}
